import { clamp } from './math';
import type { RGBA } from './types';

export const BLACK: RGBA = { r: 0, g: 0, b: 0, a: 1 };
export const WHITE: RGBA = { r: 1, g: 1, b: 1, a: 1 };
export const TRANSPARENT: RGBA = { r: 0, g: 0, b: 0, a: 0 };

function to255(value: number): number {
  return Math.round(clamp(value, 0, 1) * 255);
}

export function rgbaToHex(color: RGBA): string {
  const hex = (value: number) => to255(value).toString(16).padStart(2, '0');
  return `#${hex(color.r)}${hex(color.g)}${hex(color.b)}`;
}

export function hexToRgba(hex: string, alpha = 1): RGBA {
  const value = hex.replace('#', '').trim();
  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value.padEnd(6, '0').slice(0, 6);
  const int = Number.parseInt(full, 16);
  if (Number.isNaN(int)) return { ...BLACK, a: alpha };
  return {
    r: ((int >> 16) & 0xff) / 255,
    g: ((int >> 8) & 0xff) / 255,
    b: (int & 0xff) / 255,
    a: alpha,
  };
}

export function rgbaToCss(color: RGBA): string {
  return `rgba(${to255(color.r)}, ${to255(color.g)}, ${to255(color.b)}, ${color.a.toFixed(3)})`;
}

export function rgbaFromBytes(r: number, g: number, b: number, a = 255): RGBA {
  return { r: r / 255, g: g / 255, b: b / 255, a: a / 255 };
}

/** Perceived luminance, used to pick a readable label colour over a swatch. */
export function luminance(color: RGBA): number {
  return 0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b;
}
